import { toFormat, toUTCFormat } from "@/lib/dateHelpers";
import { Person, ProgramStatus, ProgramTypeName } from "@/types";
import {
  hasStartedPgmLevelOne,
  getQualifyingLevelMetricInput,
  isProgressVersionSupported,
  isQualifyingLevelComplete
} from "./pgm-levels";
import {
  buildLevelRequirements,
  getStatusTagColor,
  isRequirementRowSatisfied,
  type RequirementRow
} from "./pgm-requirements";
import {
  buildBackgroundCheckPathAwareTooltip,
  buildQualifyingLevelRestartTooltip,
  getQualifyingLevelStandingExpirationDate,
  isBackgroundCheckStillMetForCurrentPath,
  isQualifyingLevelStandingExpiredForProspectRestart
} from "./pathAwareExpiration";

export const ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL = "Associate or Affiliate Application";
export const AFFILIATE_APPLICATION_LABEL = "Affiliate Application";
export const ASSOCIATE_APPLICATION_LABEL = "Associate Application";
export const PURCHASE_QUALIFYING_LEVEL_LABEL = "Purchase Qualifying Level";
export const PGA_MEMBERSHIP_INTEREST_FORM_LABEL = "PGA Membership Interest Form";

type QualifyingApplicationKind = "affiliate" | "associate";

const COMPLETE_REQUIREMENT_STATUSES = new Set(["Complete", "Completed", "Passed", "Qualified", "Attended", "Approved"]);

const PRESERVED_REQUIREMENT_STATUSES = new Set(["Failed", "Denied", "Expired", "Locked"]);
const HISTORICAL_OUTCOME_STATUSES = new Set(
  [...PRESERVED_REQUIREMENT_STATUSES].filter((status) => status !== "Locked")
);

function isApprovedApplicationStatus(status?: string | null): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "approved" || normalized === "elected" || normalized === "complete";
}

function findApprovedMembershipApplication(person: Person, applicationType: QualifyingApplicationKind) {
  return (person.membershipApplications ?? []).find(
    (application) =>
      application.applicationType?.toLowerCase() === applicationType && isApprovedApplicationStatus(application.status)
  );
}

function hasAdvancedPastQualifyingLevel(person: Person): boolean {
  if (hasStartedPgmLevelOne(person)) return true;
  if (!isQualifyingLevelComplete(person)) return false;
  const progress = person.pgmProgressStatus;
  if (!progress) return false;
  if (progress.mostRecentCompletedLevelKey === "qualifying") return true;
  return !!progress.currentLevelKey && progress.currentLevelKey !== "qualifying";
}

function applicationKindFromMembershipProgram(person: Person): QualifyingApplicationKind | null {
  const name = person.latestProgram?.programType?.name;
  if (name === ProgramTypeName.Affiliate) return "affiliate";
  if (name === ProgramTypeName.Associate || name === ProgramTypeName.Member) return "associate";
  return null;
}

function labelForApplicationKind(kind: QualifyingApplicationKind): string {
  return kind === "affiliate" ? AFFILIATE_APPLICATION_LABEL : ASSOCIATE_APPLICATION_LABEL;
}

export function getCompletedQualifyingApplication(person: Person): {
  kind: QualifyingApplicationKind;
  label: string;
} | null {
  if (findApprovedMembershipApplication(person, "affiliate")) {
    return { kind: "affiliate", label: AFFILIATE_APPLICATION_LABEL };
  }

  if (findApprovedMembershipApplication(person, "associate")) {
    return { kind: "associate", label: ASSOCIATE_APPLICATION_LABEL };
  }

  if (isApprovedApplicationStatus(person.prospectProgram?.latestAssociateApplication?.status)) {
    return { kind: "associate", label: ASSOCIATE_APPLICATION_LABEL };
  }

  if (hasAdvancedPastQualifyingLevel(person)) {
    const kind = applicationKindFromMembershipProgram(person);
    if (kind) return { kind, label: labelForApplicationKind(kind) };
  }

  return null;
}

function isApplicationRequirementRow(row: RequirementRow): boolean {
  if (row.key === "application") return true;
  return /^(affiliate|associate)?\s*application$/i.test(row.name.trim());
}

function isProgressApplicationRequirementRow(row: RequirementRow): boolean {
  return isApplicationRequirementRow(row) && (row.source === "progress" || row.source === "progress-overlay");
}

function resolveApplicationCompletionDate(row: RequirementRow, person: Person): string | null {
  if (row.completionDate) return row.completionDate;

  const completed = getCompletedQualifyingApplication(person);
  const approved = completed
    ? findApprovedMembershipApplication(person, completed.kind)
    : (findApprovedMembershipApplication(person, "affiliate") ??
      findApprovedMembershipApplication(person, "associate"));
  if (approved?.approvedAt) return toFormat(approved.approvedAt);

  const ccms = person.prospectProgram?.latestAssociateApplication;
  if (ccms?.endDate) return toFormat(ccms.endDate);
  if (ccms?.startDate) return toFormat(ccms.startDate);
  return null;
}

function withResolvedApplicationRow(
  row: RequirementRow,
  person: Person,
  name: string,
  status: string = row.status
): RequirementRow {
  return {
    ...row,
    name,
    status,
    statusType: getStatusTagColor(status),
    completionDate: COMPLETE_REQUIREMENT_STATUSES.has(status)
      ? resolveApplicationCompletionDate({ ...row, status }, person)
      : row.completionDate
  };
}

function labelQualifyingApplicationRow(
  row: RequirementRow,
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow {
  if (!isApplicationRequirementRow(row)) return row;

  if (isProgressApplicationRequirementRow(row)) {
    const kind = completed?.kind ?? applicationKindFromMembershipProgram(person);
    const label = kind
      ? labelForApplicationKind(kind)
      : /affiliate/i.test(row.name)
        ? AFFILIATE_APPLICATION_LABEL
        : /associate/i.test(row.name)
          ? ASSOCIATE_APPLICATION_LABEL
          : ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL;
    return completed ? withResolvedApplicationRow(row, person, label) : { ...row, name: label };
  }

  const pastQlImpliedComplete =
    !!completed && hasAdvancedPastQualifyingLevel(person) && !COMPLETE_REQUIREMENT_STATUSES.has(row.status);

  if (!COMPLETE_REQUIREMENT_STATUSES.has(row.status) && !pastQlImpliedComplete) {
    return { ...row, name: ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL };
  }

  if (completed) {
    return withResolvedApplicationRow(row, person, completed.label, pastQlImpliedComplete ? "Complete" : row.status);
  }

  if (/affiliate/i.test(row.name)) {
    return withResolvedApplicationRow(row, person, AFFILIATE_APPLICATION_LABEL);
  }
  if (/associate/i.test(row.name)) {
    return withResolvedApplicationRow(row, person, ASSOCIATE_APPLICATION_LABEL);
  }

  return withResolvedApplicationRow(row, person, ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL);
}

function buildLevelOneAssociateApplicationRow(person: Person): RequirementRow {
  const approvedAssociate = findApprovedMembershipApplication(person, "associate");
  const ccms = person.prospectProgram?.latestAssociateApplication;
  const hasApprovedAssociate = !!approvedAssociate || isApprovedApplicationStatus(ccms?.status);
  const completionSource = approvedAssociate?.approvedAt ?? ccms?.endDate ?? null;
  const completionDate = hasApprovedAssociate && completionSource ? toFormat(completionSource) : null;
  const status = hasApprovedAssociate ? "Complete" : "Incomplete";

  return {
    id: "associate-application-level-1",
    key: "associate_application",
    name: ASSOCIATE_APPLICATION_LABEL,
    completionDate,
    status,
    statusType: getStatusTagColor(status)
  };
}

function insertBeforePurchase(rows: RequirementRow[], row: RequirementRow): RequirementRow[] {
  const purchaseIndex = rows.findIndex(
    (requirement) => requirement.key === "purchase" || /^Purchase Level/i.test(requirement.name)
  );
  if (purchaseIndex === -1) return [...rows, row];
  return [...rows.slice(0, purchaseIndex), row, ...rows.slice(purchaseIndex)];
}

export function withAffiliatePathAssociateApplication(rows: RequirementRow[], person: Person): RequirementRow[] {
  if (getCompletedQualifyingApplication(person)?.kind !== "affiliate") return rows;
  if (rows.some((row) => row.key === "associate_application" || row.name === ASSOCIATE_APPLICATION_LABEL)) {
    return rows;
  }
  return insertBeforePurchase(rows, buildLevelOneAssociateApplicationRow(person));
}

function buildPgaAccountRequirementRow(accountCreatedAt?: string | null): RequirementRow {
  const hasAccount = !!accountCreatedAt;
  const status = hasAccount ? "Complete" : "Incomplete";

  return {
    id: "pga-account",
    name: "PGA Account",
    completionDate: hasAccount ? toFormat(accountCreatedAt) : null,
    status,
    statusType: getStatusTagColor(status)
  };
}

function buildMembershipInterestFormRequirementRow(person: Person): RequirementRow | null {
  const form = person.prospectProgram?.membershipInterestForm;
  if (!form) return null;

  return {
    id: "pga-membership-interest-form",
    key: "membership_interest_form",
    name: PGA_MEMBERSHIP_INTEREST_FORM_LABEL,
    completionDate: form.startedAt ? toUTCFormat(form.startedAt) : null,
    status: "Complete",
    statusType: getStatusTagColor("Complete")
  };
}

function getQualifyingLevelEnrollmentStartDate(person: Person): string | null {
  return getQualifyingLevelMetricInput(person)?.startDate ?? null;
}

function buildPurchaseQualifyingLevelRequirementRow(person: Person): RequirementRow {
  const startDate = getQualifyingLevelEnrollmentStartDate(person);
  const hasPurchased = !!startDate;
  const status = hasPurchased ? "Complete" : "Incomplete";

  return {
    id: "purchase-qualifying-level",
    key: "purchase_qualifying_level",
    name: PURCHASE_QUALIFYING_LEVEL_LABEL,
    completionDate: hasPurchased ? toFormat(startDate) : null,
    status,
    statusType: getStatusTagColor(status)
  };
}

function normalizeQualifyingRequirementRow(row: RequirementRow): RequirementRow {
  if (isProgressApplicationRequirementRow(row)) return row;
  if (isPurchaseLevelOneRequirementRow(row) && row.status === "Open" && !row.source) return row;
  if (COMPLETE_REQUIREMENT_STATUSES.has(row.status) || PRESERVED_REQUIREMENT_STATUSES.has(row.status)) {
    return row;
  }

  return {
    ...row,
    completionDate: null,
    status: "Incomplete",
    statusType: getStatusTagColor("Incomplete")
  };
}

function isPlayingAbilityTestRequirement(row: RequirementRow): boolean {
  return row.key === "playing_ability_test" || row.key === "pat" || /playing ability test/i.test(row.name);
}

function isBackgroundCheckRequirementRow(row: RequirementRow): boolean {
  return row.key === "background_check" || row.name === "Background Check";
}

function isPurchaseLevelOneRequirementRow(row: RequirementRow): boolean {
  return row.key === "purchase" || /^Purchase Level 1/i.test(row.name);
}

function isQualifyingGateRequirementRow(row: RequirementRow): boolean {
  return (
    isBackgroundCheckRequirementRow(row) || isApplicationRequirementRow(row) || isPurchaseLevelOneRequirementRow(row)
  );
}

function hasAllQualifyingGateRows(rows: RequirementRow[]): boolean {
  return (
    rows.some(isBackgroundCheckRequirementRow) &&
    rows.some(isApplicationRequirementRow) &&
    rows.some(isPurchaseLevelOneRequirementRow)
  );
}

function buildBackgroundCheckRequirementRow(person: Person): RequirementRow {
  const backgroundCheck = person.prospectProgram?.backgroundCheck;
  const statusUpper = backgroundCheck?.status?.toUpperCase();
  const isApproved = statusUpper === "APPROVED";
  const isExpired = statusUpper === "EXPIRED";
  const stillMetDespiteExpiry = isExpired && isBackgroundCheckStillMetForCurrentPath(person);
  const status = isApproved || stillMetDespiteExpiry ? "Complete" : isExpired ? "Expired" : "Incomplete";
  const completionDateSource = backgroundCheck?.endDate ?? backgroundCheck?.startDate;
  const expiredDate = backgroundCheck?.expirationDate ?? backgroundCheck?.endDate ?? null;
  const pathAwareTooltip = isExpired
    ? buildBackgroundCheckPathAwareTooltip({
        stillMet: stillMetDespiteExpiry,
        expiredDate,
        person
      })
    : undefined;

  return {
    id: "background_check",
    key: "background_check",
    name: "Background Check",
    completionDate: (isApproved || isExpired) && completionDateSource ? toFormat(completionDateSource) : null,
    status,
    statusType: getStatusTagColor(status),
    ...(pathAwareTooltip && { statusTooltip: pathAwareTooltip })
  };
}

function buildApplicationGateRequirementRow(
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow {
  const status = completed ? "Complete" : "Incomplete";
  const row: RequirementRow = {
    id: "application",
    key: "application",
    name: completed?.label ?? ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL,
    completionDate: null,
    status,
    statusType: getStatusTagColor(status)
  };

  return {
    ...row,
    completionDate: completed ? resolveApplicationCompletionDate(row, person) : null
  };
}

function buildPurchaseLevelOneRequirementRow(person: Person): RequirementRow {
  const purchased = hasStartedPgmLevelOne(person);
  const startDate =
    person.educationPgmProgramHistory?.find((item) => item.level === 1)?.startDate ??
    person.pgmProgressStatus?.levels.find((level) => level.numeral === 1)?.startDate ??
    null;
  const status = purchased ? "Complete" : "Incomplete";

  return {
    id: "purchase",
    key: "purchase",
    name: "Purchase Level 1",
    completionDate: purchased && startDate ? toFormat(startDate) : null,
    status,
    statusType: getStatusTagColor(status)
  };
}

function buildOpenPurchaseLevelOneRequirementRow(): RequirementRow {
  return {
    id: "purchase",
    key: "purchase",
    name: "Purchase Level 1",
    completionDate: null,
    status: "Open",
    statusType: getStatusTagColor("Open")
  };
}

const PENDING_REGISTRATION_CREATED_REASONS = new Set(["pending_associate", "pending_affiliate"]);

function isPendingRegistrationPath(person: Person): boolean {
  const latestProgram = person.latestProgram;
  if (latestProgram && latestProgram.status !== ProgramStatus.ACTIVE) return false;

  const currentBandReason = latestProgram?.band?.createdReason;
  if (currentBandReason != null) {
    return PENDING_REGISTRATION_CREATED_REASONS.has(currentBandReason);
  }

  return (person.bands ?? []).some(
    (band) => !band.terminatesOn && !!band.createdReason && PENDING_REGISTRATION_CREATED_REASONS.has(band.createdReason)
  );
}

export function isOpenPurchaseLevelOnePath(
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication> = getCompletedQualifyingApplication(person)
): boolean {
  if (hasStartedPgmLevelOne(person)) return false;
  if (person.latestProgram?.programType?.name === ProgramTypeName.Member) return false;

  return (
    completed?.kind === "affiliate" ||
    completed?.kind === "associate" ||
    person.latestProgram?.programType?.name === ProgramTypeName.Affiliate ||
    isPendingRegistrationPath(person)
  );
}

function hasBackgroundCheckEvidence(person: Person): boolean {
  return !!person.prospectProgram?.backgroundCheck?.status;
}

function pickGateRow(
  fromLevel: RequirementRow[],
  match: (row: RequirementRow) => boolean,
  fallback: () => RequirementRow | null
): RequirementRow | null {
  return fromLevel.find(match) ?? fallback();
}

function isEvidenceWorthyRequirementRow(row: RequirementRow): boolean {
  return isRequirementRowSatisfied(row) || HISTORICAL_OUTCOME_STATUSES.has(row.status);
}

function withOpenPurchaseLevelOne(
  rows: RequirementRow[],
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow[] {
  if (!isOpenPurchaseLevelOnePath(person, completed)) return rows;

  let foundPurchase = false;
  const remapped = rows.map((row) => {
    if (!isPurchaseLevelOneRequirementRow(row)) return row;
    foundPurchase = true;
    if (isEvidenceWorthyRequirementRow(row)) return row;
    return buildOpenPurchaseLevelOneRequirementRow();
  });

  if (!foundPurchase) return [...remapped, buildOpenPurchaseLevelOneRequirementRow()];
  return remapped;
}

function omitMemberLeftoverPurchaseLevelOne(rows: RequirementRow[], person: Person): RequirementRow[] {
  if (person.latestProgram?.programType?.name !== ProgramTypeName.Member) return rows;
  if (hasStartedPgmLevelOne(person)) return rows;
  return rows.filter((row) => !isPurchaseLevelOneRequirementRow(row));
}

function withLockedPurchaseWhileFinishingQualifyingLevel(
  rows: RequirementRow[],
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow[] {
  if (isOpenPurchaseLevelOnePath(person, completed)) return rows;
  if (hasStartedPgmLevelOne(person)) return rows;

  return rows.map((row) => {
    if (!isPurchaseLevelOneRequirementRow(row)) return row;
    if (isEvidenceWorthyRequirementRow(row)) return row;
    return {
      ...row,
      completionDate: null,
      status: "Locked",
      statusType: getStatusTagColor("Locked")
    };
  });
}

function pickEvidencedGateRow(
  fromLevel: RequirementRow[],
  match: (row: RequirementRow) => boolean,
  fallback: () => RequirementRow | null
): RequirementRow | null {
  const existing = fromLevel.find(match);
  if (existing?.source === "progress" || existing?.source === "progress-overlay") {
    if (isPurchaseLevelOneRequirementRow(existing) && !isEvidenceWorthyRequirementRow(existing)) {
      return fallback() ?? existing;
    }
    return existing;
  }
  if (existing && isEvidenceWorthyRequirementRow(existing)) return existing;
  return fallback();
}

function buildEvidenceOnlyQualifyingBodyRows(
  fromLevel: RequirementRow[],
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow[] {
  const coursework = fromLevel
    .filter((row) => !isQualifyingGateRequirementRow(row))
    .filter(isEvidenceWorthyRequirementRow);

  return [
    pickEvidencedGateRow(fromLevel, isBackgroundCheckRequirementRow, () =>
      hasBackgroundCheckEvidence(person) ? buildBackgroundCheckRequirementRow(person) : null
    ),
    ...coursework,
    pickEvidencedGateRow(fromLevel, isApplicationRequirementRow, () =>
      completed ? buildApplicationGateRequirementRow(person, completed) : null
    ),
    pickEvidencedGateRow(fromLevel, isPurchaseLevelOneRequirementRow, () => {
      if (hasStartedPgmLevelOne(person)) return buildPurchaseLevelOneRequirementRow(person);
      if (isOpenPurchaseLevelOnePath(person, completed)) return buildOpenPurchaseLevelOneRequirementRow();
      return null;
    })
  ].filter((row): row is RequirementRow => row != null);
}

function buildActiveQualifyingBodyRows(
  fromLevel: RequirementRow[],
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow[] {
  const coursework = fromLevel.filter((row) => !isQualifyingGateRequirementRow(row));

  return [
    pickGateRow(fromLevel, isBackgroundCheckRequirementRow, () => buildBackgroundCheckRequirementRow(person)),
    ...coursework,
    pickGateRow(fromLevel, isApplicationRequirementRow, () => buildApplicationGateRequirementRow(person, completed)),
    pickGateRow(fromLevel, isPurchaseLevelOneRequirementRow, () => buildPurchaseLevelOneRequirementRow(person))
  ].filter((row): row is RequirementRow => row != null);
}

function unlockStaleQualifyingGateRow(row: RequirementRow): RequirementRow {
  if (row.status !== "Locked") return row;
  if (!isApplicationRequirementRow(row)) return row;

  return {
    ...row,
    completionDate: null,
    status: "Incomplete",
    statusType: getStatusTagColor("Incomplete")
  };
}

export function isActivelyFinishingQualifyingLevel(
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication> = getCompletedQualifyingApplication(person)
): boolean {
  if (isOpenPurchaseLevelOnePath(person, completed)) return false;
  if (hasStartedPgmLevelOne(person)) return false;

  const programName = person.latestProgram?.programType?.name;
  if (programName === ProgramTypeName.Affiliate || programName === ProgramTypeName.Member) {
    return false;
  }

  const progress = person.pgmProgressStatus;
  if (progress && isProgressVersionSupported(progress.curriculumVersion, progress.programType)) {
    if (progress.currentLevelKey === "qualifying") return true;
    const qualifying = progress.levels.find((level) => level.numeral === 0 || level.key === "qualifying");
    if (qualifying && (qualifying.status === "complete" || !!qualifying.completionDate)) {
      return true;
    }
  }

  return person.latestProgram?.programType?.name === ProgramTypeName.Prospect && isQualifyingLevelComplete(person);
}

function mergeQualifyingLevelBodyRows(
  fromLevel: RequirementRow[],
  person: Person,
  completed: ReturnType<typeof getCompletedQualifyingApplication>
): RequirementRow[] {
  let rows: RequirementRow[];
  if (isQualifyingLevelComplete(person)) {
    if (isActivelyFinishingQualifyingLevel(person, completed)) {
      rows = buildActiveQualifyingBodyRows(fromLevel, person, completed).map(unlockStaleQualifyingGateRow);
    } else {
      rows = buildEvidenceOnlyQualifyingBodyRows(fromLevel, person, completed);
    }
  } else if (hasAllQualifyingGateRows(fromLevel)) {
    rows = fromLevel;
  } else {
    rows = buildActiveQualifyingBodyRows(fromLevel, person, completed);
  }

  return withLockedPurchaseWhileFinishingQualifyingLevel(
    omitMemberLeftoverPurchaseLevelOne(withOpenPurchaseLevelOne(rows, person, completed), person),
    person,
    completed
  );
}

function buildQualifyingFramingRows(person: Person, qlComplete: boolean): RequirementRow[] {
  const membershipFormRow = buildMembershipInterestFormRequirementRow(person);

  if (!qlComplete) {
    return [
      buildPgaAccountRequirementRow(person.accountCreatedAt),
      ...(membershipFormRow ? [membershipFormRow] : []),
      buildPurchaseQualifyingLevelRequirementRow(person)
    ];
  }

  const enrollmentStart = getQualifyingLevelEnrollmentStartDate(person);
  return [
    ...(person.accountCreatedAt ? [buildPgaAccountRequirementRow(person.accountCreatedAt)] : []),
    ...(membershipFormRow ? [membershipFormRow] : []),
    ...(enrollmentStart ? [buildPurchaseQualifyingLevelRequirementRow(person)] : [])
  ];
}

function withPathAwareBackgroundCheckRow(row: RequirementRow, person: Person): RequirementRow {
  if (!isBackgroundCheckRequirementRow(row)) return row;
  if (!person.prospectProgram?.backgroundCheck) return row;
  return buildBackgroundCheckRequirementRow(person);
}

function isPgaAccountRequirementRow(row: RequirementRow): boolean {
  return row.id === "pga-account" || row.name === "PGA Account";
}

function isMembershipInterestFormRequirementRow(row: RequirementRow): boolean {
  return row.key === "membership_interest_form" || row.name === PGA_MEMBERSHIP_INTEREST_FORM_LABEL;
}

function isPurchaseQualifyingLevelRequirementRow(row: RequirementRow): boolean {
  return row.key === "purchase_qualifying_level" || row.name === PURCHASE_QUALIFYING_LEVEL_LABEL;
}

function shouldAnnotateQualifyingLevelRestart(row: RequirementRow): boolean {
  if (row.statusTooltip) return false;
  if (!COMPLETE_REQUIREMENT_STATUSES.has(row.status)) return false;
  if (isPgaAccountRequirementRow(row)) return false;
  if (isMembershipInterestFormRequirementRow(row)) return false;
  if (isBackgroundCheckRequirementRow(row)) return false;
  if (isApplicationRequirementRow(row)) return false;
  if (isPurchaseLevelOneRequirementRow(row)) return false;
  return true;
}

function withExpiredQualifyingLevelStandingTooltip(row: RequirementRow, person: Person): RequirementRow {
  if (!isQualifyingLevelStandingExpiredForProspectRestart(person)) return row;
  if (!shouldAnnotateQualifyingLevelRestart(row)) return row;

  const expiredDate = getQualifyingLevelStandingExpirationDate(person);
  const redoClause = isPurchaseQualifyingLevelRequirementRow(row)
    ? "A new Qualifying Level purchase is required to restart as a prospect."
    : "This requirement must be completed again on a new Qualifying Level if they restart as a prospect.";

  const statusTooltip = buildQualifyingLevelRestartTooltip({
    title: row.name,
    redoClause,
    expiredDate
  });

  return statusTooltip ? { ...row, statusTooltip } : row;
}

export function buildQualifyingLevelRequirements(person: Person): RequirementRow[] {
  const completedApplication = getCompletedQualifyingApplication(person);
  const qlComplete = isQualifyingLevelComplete(person);
  const fromLevel = buildLevelRequirements(person, 0, { placeTestsBeforeApplicationGates: true }).filter(
    (row) => !isPlayingAbilityTestRequirement(row)
  );

  return [
    ...buildQualifyingFramingRows(person, qlComplete),
    ...mergeQualifyingLevelBodyRows(fromLevel, person, completedApplication)
  ]
    .map(normalizeQualifyingRequirementRow)
    .map((row) => labelQualifyingApplicationRow(row, person, completedApplication))
    .map((row) => withPathAwareBackgroundCheckRow(row, person))
    .map((row) => withExpiredQualifyingLevelStandingTooltip(row, person));
}
